import React, { useState, useRef } from "react";
import Sidebar from "./Sidebar";
import { getMaps } from "./mapsApi";
import DevMenu from "./DevMenu";
import MarkerEditModal from "./MarkerEditModal";
import BulkLootspotEditor from "./BulkLootspotEditor";
import ImportLootspotModal from "./ImportLootspotModal";
import MapView from "./MapView";
import { useMarkers } from "./hooks/useMarkers";
import { useMapScale } from "./hooks/useMapScale";
import { useMapScreenshots } from "./hooks/useMapScreenshots";
import { useLootspotImportExport } from "./hooks/useLootspotImportExport";
import { useNotification } from "./hooks/useNotification";
import Notification from "./Notification";
import markerNamesData from "./assets/markername.json";
import { getOriginalMarkerFromBackend } from "./api";
import { useHideUnusedMarkers } from "./hooks/useHideUnusedMarkers";

function App() {
  const [maps, setMaps] = useState([]);
  const [selectedMap, setSelectedMap] = useState(() => {
    return localStorage.getItem("lootspot_selected_map") || "erangel";
  });
  const [showDevMenu, setShowDevMenu] = useState(false);
  const [editingMarker, setEditingMarker] = useState(null);
  const [showCoords, setShowCoords] = useState(true);
  const [showMassLootspot, setShowMassLootspot] = useState(false);
  const [showImportLootspot, setShowImportLootspot] = useState(false);
  const [batchScreenshotIndex, setBatchScreenshotIndex] = useState(null);
  const [isScreenshotting, setIsScreenshotting] = useState(false);
  const [showMarkerIds, setShowMarkerIds] = useState(false);
  const [useAlternateImages, setUseAlternateImages] = useState(() => {
    const saved = localStorage.getItem("useAlternateImages");
    return saved === null ? false : saved === "true";
  });
  const mapRefs = useRef({});

  React.useEffect(() => {
    getMaps().then(setMaps);
  }, []);

  // Dev PIN logic
  React.useEffect(() => {
    // Keyboard shortcut (3x ctrl+alt+d)
    let devMenuKeyCount = 0;
    let timeoutId;
    const handler = (e) => {
      if (e.ctrlKey && e.altKey && e.code === "KeyD") {
        e.preventDefault();
        devMenuKeyCount++;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          devMenuKeyCount = 0;
        }, 2000);
        if (devMenuKeyCount >= 3) {
          devMenuKeyCount = 0;
          askForDevPin();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  function askForDevPin() {
    const pin = window.prompt("Enter dev PIN:");
    if (pin === "2705") {
      setShowDevMenu(true);
    } else if (pin !== null) {
      alert("Incorrect PIN!");
    }
  }

  // Markeri hook
  const { markers, setMarkers, addMarker, changeMarker, removeMarker, reloadMarkers } = useMarkers(
    maps,
    selectedMap,
  );

  // Skaliranje markera
  const markerScale = useMapScale(selectedMap, maps, mapRefs);

  // Notifikacije
  const { notification, setNotification, showNotification } = useNotification();

  // Show/Hide unused markers hook
  const { showUnused, setShowUnused, forceShowAll } = useHideUnusedMarkers();

  // Screenshot hook
  const { handleScreenshotMap, handleScreenshotAllMaps } = useMapScreenshots({
    maps,
    markers,
    mapRefs,
    setNotification: showNotification,
    setIsScreenshotting,
    isScreenshotting,
    selectedMap,
    setSelectedMap,
    batchScreenshotIndex,
    setBatchScreenshotIndex,
    useAlternateImages,
  });

  // Import/export hook
  const { handleImportLootspot, handleApplyMassLootspot } = useLootspotImportExport({
    setMarkers,
    setNotification: showNotification,
  });

  // Reset markers (tim, logo, tier)
  const handleResetMarkers = async () => {
    maps.forEach((map) => {
      const key = "lootspot_markers_" + map.key;
      let local = localStorage.getItem(key);
      if (local) {
        try {
          let arr = JSON.parse(local);
          if (Array.isArray(arr)) {
            // Remove contest markers and reset regular marker data, uključujući flag
            arr = arr
              .filter((m) => !m.id.endsWith("_contest1"))
              .map((m) => ({ ...m, team: "", logo: "", tier: "", flag: "", slot: "" }));
            localStorage.setItem(key, JSON.stringify(arr));
          }
        } catch {}
      }
    });
    setMarkers((markers) =>
      markers
        .filter((m) => !m.id.endsWith("_contest1"))
        .map((m) => ({ ...m, team: "", logo: "", tier: "", flag: "", slot: "" })),
    );
    forceShowAll(); // automatski prikaži sve markere nakon reseta
    showNotification("All team, logo, tier and flag data has been reset!");
    // Reload markers from backend to restore original positions
    setTimeout(() => reloadMarkers(), 100);
  };

  // Add contest marker and open edit modal
  function handleAddContestMarker(type, baseMarker) {
    if (type !== "contest" || !baseMarker) return;
    // Find all contest markers for this base marker
    const contestMarkers = markers.filter((m) => m.id.startsWith(baseMarker.id + "_contest"));
    // Find next available contest number (avoid eslint no-loop-func warning)
    let nextNum = 1;
    const baseId = baseMarker.id;
    const existingIds = new Set(contestMarkers.map((m) => m.id));
    while (existingIds.has(`${baseId}_contest${nextNum}`)) nextNum++;
    const contestId = `${baseId}_contest${nextNum}`;
    // Use same x, y, map, but offset right
    const offset = 0.035;
    // Move original marker left and contest marker right, both from original position
    const origX = baseMarker.x ?? 0.5;
    const origY = baseMarker.y ?? 0.5;
    // Update original marker's position (left offset)
    setMarkers((prev) =>
      prev.map((m) => (m.id === baseMarker.id ? { ...m, x: origX - offset } : m)),
    );
    // Add contest marker (right offset)
    const newMarker = {
      id: contestId,
      x: origX + offset,
      y: origY,
      size: baseMarker.size ?? 1,
      markerName: baseMarker.markerName || "",
      map: baseMarker.map,
      team: "",
      logo: "",
      tier: "",
      x_offset: 0,
    };
    setMarkers((prev) => [...prev, newMarker]);
    setEditingMarker(newMarker);
  }

  // Remove marker with contest logic
  async function handleRemoveMarker(markerId, showDev = false) {
    if (markerId.endsWith("_contest1")) {
      // Find base marker id
      const baseId = markerId.replace(/_contest1$/, "");
      const baseMarker = markers.find((m) => m.id === baseId);
      if (!baseMarker) {
        setMarkers((markers) => markers.filter((m) => m.id !== markerId));
        return;
      }
      // Dohvati original marker iz baze
      try {
        const orig = await getOriginalMarkerFromBackend(baseMarker.map, baseId);
        if (orig && typeof orig.x === "number" && typeof orig.y === "number") {
          setMarkers((markers) =>
            markers
              .filter((m) => m.id !== markerId)
              .map((m) => (m.id === baseId ? { ...m, x: orig.x, y: orig.y, x_offset: 0 } : m)),
          );
        } else {
          // fallback na brisanje bez vraćanja
          setMarkers((markers) => markers.filter((m) => m.id !== markerId));
        }
      } catch (e) {
        // fallback na brisanje bez vraćanja
        setMarkers((markers) => markers.filter((m) => m.id !== markerId));
      }
    } else {
      removeMarker(markerId, showDev);
    }
  }

  const handleToggleMapImage = () => {
    setUseAlternateImages((prev) => {
      const next = !prev;
      localStorage.setItem("useAlternateImages", next);
      return next;
    });
  };

  return (
    <div className="flex min-h-screen flex-col bg-ewave-darker">
      <Sidebar
        maps={maps}
        selectedMap={selectedMap}
        onSelectMap={setSelectedMap}
        onDownloadMap={() => handleScreenshotMap(selectedMap)}
        onDownloadAll={handleScreenshotAllMaps}
        onShowMassLootspot={() => setShowMassLootspot(true)}
        onResetMarkers={handleResetMarkers}
        onApplyBulkLootspotWithFlags={() => setShowMassLootspot(true)}
        onToggleHideUnusedMarkers={() => setShowUnused((v) => !v)}
        showUnusedMarkers={showUnused}
        onToggleMapImage={handleToggleMapImage}
        useAlternateImages={useAlternateImages}
      />
      <div className="flex flex-1 flex-col items-center p-4">
        <div className="flex w-full flex-col items-center gap-16">
          <MapView
            map={(() => {
              const m = maps.find((m) => m.key === selectedMap);
              if (!m) return null;
              const img = useAlternateImages
                ? m.img.replace(/(\.webp|\.png|\.jpg)$/i, "2$1")
                : m.img;
              return { ...m, img };
            })()}
            markers={
              showUnused ? markers : markers.filter((m) => m.team || m.logo || m.flag || m.tier)
            }
            markerScale={markerScale}
            showDevMenu={showDevMenu}
            showCoords={showCoords}
            mapRefs={mapRefs}
            onMarkerChange={(changed) => changeMarker(changed, showDevMenu)}
            onMarkerRemove={(id) => handleRemoveMarker(id, showDevMenu)}
            onMarkerEdit={setEditingMarker}
            showMarkerIds={showMarkerIds}
          />
        </div>
      </div>
      {/* Dev menu */}
      {showDevMenu && (
        <DevMenu
          onSetDrag={addMarker}
          onToggleCoords={() => setShowCoords((v) => !v)}
          showCoords={showCoords}
          onClose={() => setShowDevMenu(false)}
          onOpenImportLootspot={() => setShowImportLootspot(true)}
          onToggleShowIds={() => setShowMarkerIds((v) => !v)}
          showIds={showMarkerIds}
        />
      )}
      {/* Import Lootspot Modal */}
      {showImportLootspot && (
        <ImportLootspotModal
          onImport={handleImportLootspot}
          onClose={() => setShowImportLootspot(false)}
        />
      )}
      {/* Marker edit modal */}
      {editingMarker && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.60)",
          }}
        >
          <MarkerEditModal
            marker={editingMarker}
            onSave={(changed) => {
              changeMarker(changed);
              setEditingMarker(null);
            }}
            onClose={() => setEditingMarker(null)}
            showDev={showDevMenu}
            isTemp={!!editingMarker.isTemp}
            onAddTempMarker={handleAddContestMarker}
          />
        </div>
      )}
      {/* Bulk Lootspot Modal */}
      {showMassLootspot && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.60)",
          }}
        >
          <BulkLootspotEditor
            markerNames={markerNamesData}
            markers={markers}
            onApply={(updates) => {
              handleApplyMassLootspot(updates);
              setShowMassLootspot(false);
            }}
            onClose={() => setShowMassLootspot(false)}
          />
        </div>
      )}
      {/* Notification */}
      {notification && <Notification message={notification} onClose={() => setNotification("")} />}
    </div>
  );
}

export default App;
